import React, { useEffect, useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import { useAppSelector } from '../../app/hooks';
import useOrderCheckRetry from '../../hooks/useOrderCheckRetry.hooks';
import {
  OrderPipelineState,
  selectMenuModuleCartErrors,
  selectOrderError,
  selectOrderPipeline,
} from '../../selectors/order';

/**
 * Persistent cart-top strip showing the order pipeline's current state
 * (HITL-1527): appears when the session's first order event goes out and stays
 * until the session ends, for agent and AI orders, non-final and final.
 *
 * Compact single-row layout (demo feedback): an origin badge (who submitted —
 * agent or AI) leads the row, the message with its live elapsed carries the
 * words, and a three-segment stage RING on the right carries the state: the
 * segments are Sent, Acknowledged, Confirmed (clockwise from the top), filled
 * up to the stage the round trip reached — green while healthy, orange when
 * stuck, red on failure (still showing the reached stage), with a check or
 * cross marking the terminal states. Static, never spinning. Retry is an
 * icon button at the far edge that only appears when the order is stuck. No
 * info icon, no stage labels, constant background — height is a single line
 * except when the menu-module error list adds one.
 *
 * The pipeline state comes from selectOrderPipeline — the single derivation —
 * and the stuck/retry behavior (overdue clock, auto-resend countdown,
 * takeover click) is exactly useOrderCheckRetry's, unchanged. The plain Retry
 * alert (OrderCheckRetryAlert) renders instead when the order_status_banner
 * flag is off — see OrderReview.
 */

// Live seconds since `sentAt`, ticking only while `active`. Computed during
// render (the interval only forces re-renders) so a new order's counter is
// correct on its very first frame. 1-indexed: a fresh order reads (1s).
const useElapsedSeconds = (sentAt: string | undefined, active: boolean) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!sentAt || !active) {
      return undefined;
    }
    const intervalId = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(intervalId);
  }, [sentAt, active]);
  if (!sentAt || !active) {
    return null;
  }
  return Math.max(
    1,
    Math.ceil((Date.now() - new Date(sentAt).getTime()) / 1000)
  );
};

type RingTone = 'ok' | 'warning' | 'error';
type RingMark = 'none' | 'check' | 'cross';

const RING_COLORS: Record<RingTone, string> = {
  ok: '#6cbf3f',
  warning: '#e07a1f',
  error: '#e04255',
};
const RING_TRACK = '#c9d7e2';

// r=8 → circumference ≈ 50.27, split into three equal arcs with small gaps:
// 1st = Sent, 2nd = Acknowledged, 3rd = Confirmed (clockwise from the top).
const CIRCUMFERENCE = 2 * Math.PI * 8;
const SEGMENT_GAP = 3;
const SEGMENT_LENGTH = CIRCUMFERENCE / 3 - SEGMENT_GAP;

function StageRing({
  stage,
  tone,
  mark,
}: {
  stage: 1 | 2 | 3; // how many segments are filled
  tone: RingTone;
  mark: RingMark;
}) {
  const arc = RING_COLORS[tone];
  return (
    <Box
      component="svg"
      width={20}
      height={20}
      viewBox="0 0 20 20"
      data-testid="order-status-ring"
      data-stage={stage}
      data-tone={tone}
      data-mark={mark}
      aria-hidden="true"
      sx={{ flex: 'none' }}
    >
      {[0, 1, 2].map((i) => (
        <circle
          key={i}
          cx="10"
          cy="10"
          r="8"
          fill="none"
          stroke={i < stage ? arc : RING_TRACK}
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray={`${SEGMENT_LENGTH} ${
            CIRCUMFERENCE - SEGMENT_LENGTH
          }`}
          strokeDashoffset={-(i * CIRCUMFERENCE) / 3 - SEGMENT_GAP / 2}
          transform="rotate(-90 10 10)"
        />
      ))}
      {mark === 'check' && (
        <path
          d="M6.5 10.2l2.3 2.3 4.7-4.8"
          fill="none"
          stroke="#3b6d11"
          strokeWidth="2"
          strokeLinecap="round"
        />
      )}
      {mark === 'cross' && (
        <path
          d="M7.2 7.2l5.6 5.6M12.8 7.2l-5.6 5.6"
          fill="none"
          stroke="#a32d2d"
          strokeWidth="2"
          strokeLinecap="round"
        />
      )}
    </Box>
  );
}

export default function OrderStatusBanner({
  isUnifiedMenu,
  menuUnificationOrderFailureCallback,
  menuUnificationOrderSuccessCallback,
}: {
  isUnifiedMenu: boolean;
  menuUnificationOrderFailureCallback: Function;
  menuUnificationOrderSuccessCallback: Function;
}) {
  const pipeline = useAppSelector(selectOrderPipeline);
  const cartErrors = useAppSelector(selectMenuModuleCartErrors);
  const orderError = useAppSelector(selectOrderError);
  const { isCheckOverdue, isRetrying, autoRetrySecondsRemaining, retry } =
    useOrderCheckRetry({
      isUnifiedMenu,
      menuUnificationOrderFailureCallback,
      menuUnificationOrderSuccessCallback,
    });

  const isAwaitingPos =
    pipeline?.state === OrderPipelineState.sending ||
    pipeline?.state === OrderPipelineState.acked;
  const elapsedSeconds = useElapsedSeconds(
    pipeline?.sentAt,
    isAwaitingPos || isCheckOverdue
  );

  if (!pipeline) {
    return null; // No order sent this session yet.
  }

  const elapsedSuffix = elapsedSeconds != null ? ` (${elapsedSeconds}s)` : '';
  const orderNoun = pipeline.isFinal ? 'final order' : 'order';
  const orderTitle = pipeline.isFinal ? 'Final order' : 'Order';

  let message: string;
  let tone: RingTone;
  let mark: RingMark = 'none';
  let showRetry = false;

  if (pipeline.state === OrderPipelineState.sendFailed) {
    // Outranks the stuck warning: a Retry would re-fail at the menu module
    // until the offending items are fixed. The order-level error list
    // (formerly the separate OrderErrorSummary alert, HITL-1457) renders in
    // this strip; the per-line red alerts stay on the cart lines — they
    // locate the item and carry the click-to-send customer TTS.
    tone = 'error';
    mark = 'cross';
    message = 'Order not sent — fix the errors below';
  } else if (isCheckOverdue) {
    tone = 'warning';
    message =
      autoRetrySecondsRemaining != null
        ? `${orderTitle} unconfirmed${elapsedSuffix} — resending in ${autoRetrySecondsRemaining}s`
        : `${orderTitle} unconfirmed${elapsedSuffix}`;
    showRetry = true;
  } else {
    switch (pipeline.state) {
      case OrderPipelineState.disconnected:
        tone = 'error';
        mark = 'cross';
        message = 'POS disconnected — resends on reconnect';
        break;
      case OrderPipelineState.rejected:
        tone = 'error';
        mark = 'cross';
        message = 'POS rejected — final order already received';
        break;
      case OrderPipelineState.failed:
        tone = 'error';
        mark = 'cross';
        message = `POS error on the ${orderNoun}`;
        break;
      case OrderPipelineState.confirmed:
        tone = 'ok';
        mark = 'check';
        message = `${orderTitle} confirmed${
          pipeline.total ? ` · $${pipeline.total}` : ''
        }`;
        break;
      case OrderPipelineState.acked:
        tone = 'ok';
        message = `POS acknowledged — awaiting check${elapsedSuffix}`;
        break;
      case OrderPipelineState.sending:
      default:
        tone = 'ok';
        message = `${orderTitle} sent — awaiting POS${elapsedSuffix}`;
        break;
    }
  }

  const isAI = pipeline.origin === 'AI';
  const showPosError =
    (pipeline.state === OrderPipelineState.failed ||
      pipeline.state === OrderPipelineState.rejected) &&
    orderError;

  return (
    <Box
      role="status"
      // Pinned like the Retry alert it replaces: visible on long carts, items
      // scroll underneath (HITL-1483). Constant background in every state —
      // the ring and copy carry the state.
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        marginBottom: '8px',
        padding: '7px 12px',
        borderRadius: '8px',
        backgroundColor: '#e7f1fa',
        color: '#14344f',
        fontSize: '14px',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Tooltip title={isAI ? 'AI order' : 'Agent order'}>
          <Box
            aria-label={isAI ? 'AI order' : 'Agent order'}
            data-testid="order-origin-badge"
            data-origin={isAI ? 'AI' : 'AGENT'}
            sx={{
              width: 24,
              height: 24,
              flex: 'none',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: isAI ? '#e3e0fa' : '#d3e5f5',
              color: isAI ? '#534ab7' : '#0c447c',
            }}
          >
            {isAI ? (
              <SmartToyOutlinedIcon sx={{ fontSize: 16 }} />
            ) : (
              <PersonOutlineIcon sx={{ fontSize: 16 }} />
            )}
          </Box>
        </Tooltip>
        <Box component="span" sx={{ flex: 1, lineHeight: 1.45 }}>
          {message}
        </Box>
        <StageRing stage={pipeline.stage} tone={tone} mark={mark} />
        {showRetry && (
          <Tooltip title="Resend the order now">
            <span>
              <IconButton
                aria-label="Retry"
                size="small"
                onClick={retry}
                disabled={isRetrying}
                // Same height as the stage ring next to it, so the right edge
                // reads as one 20px cluster.
                sx={{
                  width: 20,
                  height: 20,
                  color: '#ed6c02',
                  border: '1px solid rgba(237, 108, 2, 0.55)',
                }}
              >
                <ReplayIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
      {pipeline.state === OrderPipelineState.sendFailed && (
        // Left edge aligns with the message text (badge 24px + 10px gap).
        <Box
          component="ul"
          sx={{
            margin: '2px 0 0 34px',
            padding: 0,
            listStylePosition: 'inside',
            fontFamily: 'monospace',
            fontSize: '12px',
          }}
        >
          {cartErrors.map((cartError) => (
            <li key={cartError.nodePath.join(' > ') || cartError.message}>
              {cartError.nodePath.length > 0
                ? `${cartError.reasonCode}: ${cartError.nodePath.join(' › ')}`
                : cartError.message}
            </li>
          ))}
        </Box>
      )}
      {showPosError && (
        <Box sx={{ marginLeft: '34px', marginTop: '2px', fontSize: '12.5px' }}>
          {orderError}
        </Box>
      )}
    </Box>
  );
}
